// ─────────────────────────────────────────────────────────────────────────────
// The class ocean's map. Each class has its own, generated from the class's
// seed (classrooms.ocean_seed): every student in a class explores the same
// ocean, and no two classes get the same one.
//
// A map is a long row of areas starting with the Sunlit Shallows (where you
// start), then Seagrass Meadows, Coral Reefs (mounds, and a rock arch as
// scenery), Kelp Forests, Old Shipwrecks (with a cabin to swim into) and Deep
// Trenches, several of each, in an order and at widths the seed decides.
//
// Underground:
//   • three cave systems, each three chambers (Crystal Grotto, Echo Chamber,
//     Deep Hollow) joined by tunnels, with ways in from the sea floor, and side
//     tunnels to hidden pockets
//   • far below two of them, a Sunken Temple: brick-walled rooms and corridors
//     with tougher beasts and treasure chests, and at the bottom the Abyss,
//     where the Kraken lives
//
// The solid parts are drawn with canvas paths, and the same paths make the
// collision grid, so what you see is what you bump. Every generated map is
// checked: a fish must be able to reach every pearl, sign and chest from the
// start, or the next variation is tried.
//
// Pearl, chest and area ids are stored in the database, so a seed must always
// produce the same map: never change what generate() does for existing seeds
// without clearing those classes' progress, chests and blocks.
// (2026-10-06: the map grew 5×; every class's ocean was reset with it.)
// ─────────────────────────────────────────────────────────────────────────────

/** Small deterministic random numbers. */
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Ellipse = { x: number; y: number; rx: number; ry: number };
export type Tunnel = { pts: [number, number][]; w: number };
export type Rect = { x: number; y: number; w: number; h: number };
type ZoneId = 'shallows' | 'meadow' | 'reef' | 'kelp' | 'wreck' | 'trench';
type ChamberId = 'grotto' | 'echo' | 'hollow';
type Zone = { id: ZoneId; x0: number; x1: number };
type Arch = { x0: number; x1: number; top: number; hole: { x0: number; x1: number; top: number } };
type Hull = { pts: [number, number][]; deck: [[number, number], [number, number]]; cabin: Rect; door: Rect };

export type AreaId = ZoneId | 'caves' | ChamberId | 'cabin' | 'temple' | 'abyss';
export type Area = { id: AreaId; inside: (x: number, y: number) => boolean };
export type Pearl = { id: string; x: number; y: number };
export type Sign = { i: number; x: number; y: number; style: 'post' | 'stone' | 'bottle' };
/** A treasure chest: tier 1 in caves, wrecks and trenches; 2 in the temples; 3 in the Abyss. */
export type Chest = { id: string; x: number; y: number; tier: 1 | 2 | 3 };
export type DecorKind =
  | 'grass' | 'rock' | 'star' | 'shell' | 'branch' | 'fan' | 'brain' | 'anemone' | 'sponge'
  | 'kelp' | 'crystal' | 'stalactite' | 'mast' | 'barrel' | 'anchor' | 'vent' | 'worms' | 'glow' | 'pillar' | 'urn';
export type Decor = { kind: DecorKind; x: number; y: number; s: number; seed: number; color: number; up?: boolean };

type Temple = { box: Rect; rooms: Rect[]; halls: Rect[]; abyss: Rect };
/** A stone door sealing the shaft into a temple; it opens (for each student) once they read its inscription. */
export type Gate = { i: number; x: number; y: number; w: number; h: number };
export type Npc = { id: 'turtle' | 'captain'; x: number; y: number };

/** Everything that makes one map: numbers only. */
type Def = {
  w: number; h: number; spawn: { x: number; y: number };
  floor: (x: number) => number;
  zones: Zone[]; mounds: Ellipse[]; ledges: Ellipse[]; arches: Arch[]; hulls: Hull[];
  chambers: { id: ChamberId; e: Ellipse }[]; pockets: Ellipse[]; tunnels: Tunnel[]; entrances: number[];
  caveBoxes: Rect[]; temples: Temple[]; gates: Gate[]; npcs: Npc[]; lamps: [number, number][];
  pearls: [number, number][]; signs: Sign[]; chests: Omit<Chest, 'id'>[]; decorSeed: number;
};

export function chamberFloor(c: Ellipse, x: number): number {
  return c.y + c.ry * Math.sqrt(Math.max(0, 1 - ((x - c.x) / c.rx) ** 2));
}
export function chamberCeiling(c: Ellipse, x: number): number {
  return c.y - c.ry * Math.sqrt(Math.max(0, 1 - ((x - c.x) / c.rx) ** 2));
}

/** A sea floor through control points (Catmull-Rom), with a seeded wobble that stays off steep walls. */
function makeFloor(pts: [number, number][], steep: [number, number][], phase: [number, number, number]) {
  const P = pts;
  const base = (x: number) => {
    // Binary search: the map is long.
    let lo = 0, hi = P.length - 2;
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (P[m][0] < x) lo = m; else hi = m - 1; }
    const i = lo;
    const p0 = P[Math.max(0, i - 1)], p1 = P[i], p2 = P[i + 1], p3 = P[Math.min(P.length - 1, i + 2)];
    const t = Math.min(1, Math.max(0, (x - p1[0]) / (p2[0] - p1[0])));
    const t2 = t * t, t3 = t2 * t;
    return 0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2
      + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
  };
  return (x: number) => {
    const w = steep.some(([a, b]) => x > a && x < b) ? 0.3 : 1;
    return base(x) + w * (14 * Math.sin(x / 97 + phase[0]) + 9 * Math.sin(x / 41 + phase[1]) + 5 * Math.sin(x / 17 + phase[2]));
  };
}

const inRect = (r: Rect, x: number, y: number, pad = 0) => x > r.x - pad && x < r.x + r.w + pad && y > r.y - pad && y < r.y + r.h + pad;

// ── A generated map ─────────────────────────────────────────────────────────

const WIDTH: Record<ZoneId, [number, number]> = {
  shallows: [1400, 1700], meadow: [1000, 1400], reef: [1700, 2100], kelp: [1100, 1500], wreck: [1300, 1600], trench: [1050, 1250],
};
const DEPTH: Record<Exclude<ZoneId, 'trench'>, [number, number]> = {
  shallows: [980, 1050], meadow: [1120, 1220], reef: [1220, 1320], kelp: [1400, 1540], wreck: [1820, 1930],
};
/** After the shallows: this many of each, shuffled (about 21000 units across in all). */
const POOL: [ZoneId, number][] = [['meadow', 4], ['reef', 3], ['kelp', 3], ['wreck', 2], ['trench', 2]];
const H = 7200;

function generate(seed: number): Def {
  const r = rng(seed);
  const rr = (a: number, b: number) => a + r() * (b - a);
  const shuffle = <T,>(a: T[]) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };

  // Areas, left to right: the shallows first, never two alike side by side, no trench right after the shallows.
  const bag = POOL.flatMap(([id, n]) => Array<ZoneId>(n).fill(id));
  let order: ZoneId[] = [];
  for (let tries = 0; tries < 200; tries++) {
    order = ['shallows', ...shuffle(bag)];
    if (order[1] !== 'trench' && order.every((z, i) => i === 0 || z !== order[i - 1])) break;
  }
  const zones: Zone[] = [];
  let x = 0;
  for (const id of order) { const w = Math.round(rr(...WIDTH[id])); zones.push({ id, x0: x, x1: x + w }); x += w; }
  const W = x;
  const zoneAt = (x: number) => zones.find(z => x >= z.x0 && x < z.x1) ?? zones[zones.length - 1];
  const of = (id: ZoneId) => zones.filter(z => z.id === id);

  // The floor: a gently rolling line at each area's depth; trenches drop between steep walls.
  const pts: [number, number][] = [];
  const steep: [number, number][] = [];
  zones.forEach((z, i) => {
    if (z.id !== 'trench') {
      const base = rr(...DEPTH[z.id]);
      for (let px = z.x0 + 40; px < z.x1 - 40; px += rr(300, 380)) pts.push([px, base + rr(-45, 45)]);
      return;
    }
    const left = pts.length ? pts[pts.length - 1][1] : 1100;
    const next = zones[i + 1];
    const right = next && next.id !== 'trench' ? rr(...DEPTH[next.id]) : rr(2100, 2200);
    const bottom = rr(3500, 3800);
    pts.push([z.x0 + 40, left + 30], [z.x0 + 120, left + 260], [z.x0 + 200, bottom - 420], [z.x0 + 320, bottom],
      [z.x1 - 320, bottom + 15], [z.x1 - 190, bottom - 500], [z.x1 - 110, right + 260], [z.x1 - 30, right + 30]);
    steep.push([z.x0 + 60, z.x1 - 60]);
  });
  pts.unshift([0, pts[0][1]]);
  pts.push([W, pts[pts.length - 1][1]]);
  const floor = makeFloor(pts, steep, [rr(0, 6.28), rr(0, 6.28), rr(0, 6.28)]);
  const maxFloor = (a: number, b: number) => { let m = 0; for (let px = a; px <= b; px += 20) m = Math.max(m, floor(px)); return m; };
  const minFloor = (a: number, b: number) => { let m = 1e9; for (let px = a; px <= b; px += 20) m = Math.min(m, floor(px)); return m; };
  const nearTrench = (x: number, pad: number) => of('trench').some(t => x + pad > t.x0 - 200 && x - pad < t.x1 + 200);

  // Each reef: mounds, and an arch in a gap between them.
  const mounds: Ellipse[] = [];
  const arches: Arch[] = [];
  for (const reef of of('reef')) {
    const ms: Ellipse[] = [];
    for (let mx = reef.x0 + 220 + rr(0, 150); mx < reef.x1 - 220 && ms.length < 5; mx += rr(380, 560)) ms.push({ x: mx, y: floor(mx), rx: rr(110, 170), ry: rr(70, 110) });
    const edges = [reef.x0 + 60, ...ms.flatMap(m => [m.x - m.rx - 40, m.x + m.rx + 40]), reef.x1 - 60];
    const gaps: [number, number][] = [];
    for (let i = 0; i < edges.length; i += 2) if (edges[i + 1] - edges[i] >= 360) gaps.push([edges[i], edges[i + 1]]);
    if (!gaps.length && ms.length > 2) { const m = ms.splice(1, 1)[0]; gaps.push([m.x - 180, m.x + 180]); }
    if (gaps.length) {
      const [g0, g1] = gaps[Math.floor(r() * gaps.length)];
      const w = rr(300, 340);
      const ax0 = g0 + rr(0, g1 - g0 - w), ax1 = ax0 + w;
      const top = minFloor(ax0, ax1) - rr(240, 290);
      const mid = (ax0 + ax1) / 2;
      arches.push({ x0: ax0, x1: ax1, top, hole: { x0: mid - 75, x1: mid + 75, top: top + 110 } });
    }
    mounds.push(...ms);
  }

  // Each wreck, bow left or right, with its cabin and doorway.
  const hulls: Hull[] = [];
  const hullSpans: [number, number][] = [];
  for (const wz of of('wreck')) {
    const dir = r() < 0.5 ? 1 : -1;
    const cx = rr(wz.x0 + 460, wz.x1 - 460);
    const base = floor(cx);
    const top: [number, number][] = [[cx - 320 * dir, floor(cx - 320 * dir) + 8], [cx - 270 * dir, base - 170], [cx + 270 * dir, base - 230], [cx + 320 * dir, base - 150], [cx + 300 * dir, floor(cx + 300 * dir) + 8]];
    const bottom: [number, number][] = [];
    for (let k = 1; k < 15; k++) { const bx = cx + 300 * dir - (620 * dir * k) / 15; bottom.push([bx, floor(bx) + 14]); }
    hulls.push({
      pts: [...top, ...bottom], deck: [top[1], top[2]],
      cabin: { x: dir === 1 ? cx - 130 : cx - 100, y: base - 172, w: 230, h: 102 },
      door: { x: dir === 1 ? cx + 80 : cx - 340, y: base - 150, w: 260, h: 60 },
    });
    hullSpans.push([cx - 380, cx + 380]);
  }

  const blockedFloor = (px: number) =>
    hullSpans.some(([a, b]) => px > a && px < b) || arches.some(a => px > a.x0 - 60 && px < a.x1 + 60) ||
    mounds.some(m => Math.abs(px - m.x) < m.rx + 40) || zoneAt(px).id === 'trench' || px < 300 || px > W - 300;

  // ── Caves: three systems, one in each third of the map ──
  const chambers: { id: ChamberId; e: Ellipse }[] = [];
  const pockets: Ellipse[] = [];
  const tunnels: Tunnel[] = [];
  const entrances: number[] = [];
  const caveBoxes: Rect[] = [];
  const systems: { id: ChamberId; e: Ellipse }[][] = [];
  const below = (px: number, py: number) => Math.max(py, floor(px) + 230);
  for (let s = 0; s < 3; s++) {
    const a = W * (0.06 + s * 0.32), b = W * (0.32 + s * 0.32);
    const centres: number[] = [];
    for (let tries = 0; tries < 400 && centres.length < 3; tries++) {
      const cxx = rr(a + 300, b - 300);
      if (nearTrench(cxx, 400) || centres.some(c => Math.abs(c - cxx) < 900)) continue;
      centres.push(cxx);
    }
    centres.sort((p, q) => p - q);
    const names = shuffle<ChamberId>(['grotto', 'echo', 'hollow']);
    const sys = centres.map((cxx, i) => {
      const rx = rr(280, 340), ry = rr(170, 215);
      const y = Math.min(3300 - ry, maxFloor(cxx - rx - 60, cxx + rx + 60) + ry + rr(420, 900));
      return { id: names[i], e: { x: cxx, y, rx, ry } };
    });
    if (!sys.length) continue;
    systems.push(sys);
    chambers.push(...sys);
    for (let i = 0; i < sys.length - 1; i++) {
      const p = sys[i].e, q = sys[i + 1].e;
      const st: [number, number] = [p.x + p.rx * 0.8, p.y + rr(-0.3, 0.4) * p.ry];
      const en: [number, number] = [q.x - q.rx * 0.8, q.y + rr(-0.3, 0.4) * q.ry];
      const n = Math.abs(en[0] - st[0]) > 1500 ? 2 : 1;
      const mids: [number, number][] = [];
      for (let k = 1; k <= n; k++) {
        const mx = st[0] + ((en[0] - st[0]) * k) / (n + 1) + rr(-120, 120);
        mids.push([mx, Math.min(3400, below(mx, st[1] + ((en[1] - st[1]) * k) / (n + 1) + rr(-200, 200)))]);
      }
      tunnels.push({ pts: [st, ...mids, en], w: rr(130, 150) });
    }
    // Two ways in from the floor, from the first and last chambers.
    for (const ci of sys.length > 1 ? [0, sys.length - 1] : [0]) {
      const c = sys[ci].e;
      for (let tries = 0; tries < 90; tries++) {
        const reach = 300 + tries * 10;
        const xe = c.x + rr(-reach, reach);
        if (blockedFloor(xe) || entrances.some(e => Math.abs(e - xe) < 400)) continue;
        const fy = floor(xe);
        tunnels.push({ pts: [[xe, fy - 70], [xe + rr(-60, 60), fy + (c.y - c.ry - fy) * 0.5], [c.x + rr(-0.35, 0.35) * c.rx, c.y - c.ry * 0.65]], w: 150 });
        entrances.push(xe);
        break;
      }
    }
    // Side tunnels down to hidden pockets.
    for (const c of sys.slice(0, 2)) {
      const side = r() < 0.5 ? -1 : 1;
      const end: [number, number] = [c.e.x + side * rr(450, 700), c.e.y + rr(380, 620)];
      const pk: Ellipse = { x: end[0], y: end[1], rx: rr(130, 170), ry: rr(90, 110) };
      tunnels.push({ pts: [[c.e.x + side * c.e.rx * 0.5, c.e.y + c.e.ry * 0.6], [c.e.x + side * rr(220, 360), c.e.y + rr(260, 380)], [pk.x - side * pk.rx * 0.6, pk.y]], w: rr(110, 125) });
      pockets.push(pk);
    }
    const xs = [...sys.flatMap(c => [c.e.x - c.e.rx, c.e.x + c.e.rx]), ...pockets.slice(-2).flatMap(p => [p.x - p.rx, p.x + p.rx])];
    const ys = [...sys.flatMap(c => [c.e.y - c.e.ry, c.e.y + c.e.ry]), ...pockets.slice(-2).flatMap(p => [p.y - p.ry, p.y + p.ry])];
    caveBoxes.push({ x: Math.min(...xs) - 80, y: Math.min(...ys) - 120, w: Math.max(...xs) - Math.min(...xs) + 160, h: Math.max(...ys) - Math.min(...ys) + 240 });
  }

  // ── Two Sunken Temples, under the first and last cave systems ──
  const temples: Temple[] = [];
  const gates: Gate[] = [];
  const lamps: [number, number][] = [];
  for (const sys of systems.length >= 2 ? [systems[0], systems[systems.length - 1]] : systems) {
    const cols = 4 + (r() < 0.5 ? 1 : 0), rows = 3;
    const RW = 380, RH = 250, GX = 560, GY = 400;
    const deepest = sys.reduce((m, c) => (c.e.y > m.e.y ? c : m), sys[0]).e;
    const x0 = Math.max(400, Math.min(W - 400 - cols * GX, deepest.x - (cols * GX) / 2));
    const y0 = rr(4100, 4400);
    const rooms: Rect[] = [];
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
      const w = RW + rr(-40, 60), h = RH + rr(-20, 40);
      rooms.push({ x: x0 + i * GX + rr(0, GX - w - 60), y: y0 + j * GY + rr(0, GY - h - 50), w, h });
    }
    const at = (i: number, j: number) => rooms[j * cols + i];
    const halls: Rect[] = [];
    // Halls run between two rooms where they overlap, wide enough to swim through.
    const joinH = (p: Rect, q: Rect) => {
      const top = Math.max(p.y, q.y), h = Math.max(80, Math.min(120, Math.min(p.y + p.h, q.y + q.h) - top - 30));
      halls.push({ x: p.x + p.w - 20, y: top + 15, w: q.x - p.x - p.w + 40, h });
    };
    const joinV = (p: Rect, q: Rect) => {
      const left = Math.max(p.x, q.x), w = Math.max(90, Math.min(130, Math.min(p.x + p.w, q.x + q.w) - left - 30));
      halls.push({ x: left + 15, y: p.y + p.h - 20, w, h: q.y - p.y - p.h + 40 });
    };
    // Each row a corridor of rooms; two ways down between rows, so there are loops.
    for (let j = 0; j < rows; j++) for (let i = 0; i < cols - 1; i++) joinH(at(i, j), at(i + 1, j));
    for (let j = 0; j < rows - 1; j++) {
      const downs = shuffle(Array.from({ length: cols }, (_, i) => i)).slice(0, 2);
      for (const i of downs) joinV(at(i, j), at(i, j + 1));
    }
    // The Abyss: a great hall below the bottom row, down a long shaft.
    const bottomRoom = at(Math.floor(cols / 2), rows - 1);
    const abyss: Rect = { x: bottomRoom.x + bottomRoom.w / 2 - 420, y: y0 + rows * GY + 320, w: 840, h: 460 };
    halls.push({ x: bottomRoom.x + bottomRoom.w / 2 - 70, y: bottomRoom.y + bottomRoom.h - 20, w: 140, h: abyss.y - bottomRoom.y - bottomRoom.h + 40 });
    // The way in: a winding shaft from the deepest chamber of the caves above down to the top row.
    const gate = at(Math.floor(r() * cols), 0);
    const gx = gate.x + gate.w / 2;
    tunnels.push({ pts: [[deepest.x + rr(-0.3, 0.3) * deepest.rx, deepest.y + deepest.ry * 0.7], [deepest.x + (gx - deepest.x) * 0.3 + rr(-150, 150), deepest.y + 500], [gx + rr(-200, 200), (deepest.y + gate.y) / 2 + 300], [gx, gate.y + 60]], w: 140 });
    gates.push({ i: temples.length, x: gx - 110, y: gate.y - 110, w: 220, h: 104 });
    for (const rm of rooms) if (r() < 0.6) lamps.push([rm.x + rm.w * rr(0.25, 0.75), rm.y - 16]);
    lamps.push([abyss.x + 120, abyss.y - 16], [abyss.x + abyss.w - 120, abyss.y - 16]);
    const all = [...rooms, abyss];
    const bx0 = Math.min(...all.map(q => q.x)) - 80, by0 = Math.min(...all.map(q => q.y)) - 80;
    temples.push({ box: { x: bx0, y: by0, w: Math.max(...all.map(q => q.x + q.w)) + 80 - bx0, h: abyss.y - by0 - 40 }, rooms, halls, abyss });
  }

  // Trench ledges, sticking out of each wall.
  const ledges: Ellipse[] = [];
  for (const trench of of('trench')) {
    const bottomY = floor((trench.x0 + trench.x1) / 2);
    const wallAt = (y: number, fromLeft: boolean) => {
      if (fromLeft) { for (let px = trench.x0; px < trench.x0 + 420; px += 2) if (floor(px) >= y) return px; }
      else { for (let px = trench.x1; px > trench.x1 - 420; px -= 2) if (floor(px) >= y) return px; }
      return null;
    };
    for (const fromLeft of [true, false]) {
      const rim = floor(fromLeft ? trench.x0 : trench.x1 - 1);
      const ys: number[] = [];
      for (let k = 0; k < 3; k++) {
        const y = rr(rim + 300, bottomY - 380);
        if (ys.some(v => Math.abs(v - y) < 320)) continue;
        const wx = wallAt(y, fromLeft);
        if (wx == null) continue;
        ys.push(y);
        ledges.push({ x: wx + (fromLeft ? 45 : -50), y, rx: rr(95, 115), ry: rr(24, 30) });
      }
    }
  }

  const holeNear = (px: number) => entrances.some(e => Math.abs(px - e) < 140);

  // Treasure chests.
  const chests: Omit<Chest, 'id'>[] = [];
  const onFloorOf = (e: Ellipse, x: number) => ({ x, y: chamberFloor(e, x) - 6, tier: 1 as const });
  for (const { e } of chambers) chests.push(onFloorOf(e, e.x + rr(-0.4, 0.4) * e.rx));
  for (const pk of pockets) chests.push(onFloorOf(pk, pk.x));
  for (const h of hulls) chests.push({ x: h.cabin.x + h.cabin.w / 2, y: h.cabin.y + h.cabin.h - 4, tier: 1 });
  for (const t of of('trench')) { const cx = (t.x0 + t.x1) / 2 + rr(-80, 80); chests.push({ x: cx, y: floor(cx) - 4, tier: 1 }); }
  for (const t of temples) {
    for (const rm of t.rooms) if (r() < 0.65) chests.push({ x: rm.x + rm.w * rr(0.2, 0.8), y: rm.y + rm.h - 4, tier: 2 });
    chests.push({ x: t.abyss.x + t.abyss.w * 0.3, y: t.abyss.y + t.abyss.h - 4, tier: 3 }, { x: t.abyss.x + t.abyss.w * 0.7, y: t.abyss.y + t.abyss.h - 4, tier: 3 });
  }

  // Pearls: 60, mostly somewhere hard to reach.
  const pearls: [number, number][] = [];
  for (const { e } of chambers) pearls.push([e.x - e.rx * 0.6, chamberFloor(e, e.x - e.rx * 0.6) - 18], [e.x + e.rx * 0.6, chamberFloor(e, e.x + e.rx * 0.6) - 18], [e.x, chamberCeiling(e, e.x) + 40]);
  for (const t of tunnels) if (t.pts.length > 2) pearls.push([t.pts[1][0], t.pts[1][1]]);
  for (const h of hulls) pearls.push([h.cabin.x + 50, h.cabin.y + h.cabin.h - 18], [h.cabin.x + h.cabin.w - 50, h.cabin.y + h.cabin.h - 18]);
  for (const t of of('trench')) { const tm = (t.x0 + t.x1) / 2; for (const off of [-170, 160]) pearls.push([tm + off, floor(tm + off) - 18]); }
  for (const l of ledges) pearls.push([l.x, l.y - l.ry - 18]);
  for (const t of temples) for (const rm of t.rooms.slice(0, 4)) pearls.push([rm.x + rm.w / 2, rm.y + 60]);
  for (let k = 0; k < 4; k++) pearls.push([rr(200, W - 200), rr(110, 560)]);
  for (let tries = 0; pearls.length < 60 && tries < 400; tries++) {
    const px = rr(300, W - 300);
    if (holeNear(px) || blockedFloor(px)) continue;
    pearls.push([px, floor(px) - 18]);
  }
  pearls.length = Math.min(pearls.length, 60);

  // Signs, each where its message makes sense (texts in lang.ts, by number).
  const spawnX = rr(650, 1000);
  const signs: Sign[] = [{ i: 0, x: spawnX, y: floor(spawnX), style: 'post' }];
  const post = (i: number, px: number, style: Sign['style'] = 'post') => { if (!holeNear(px) && !blockedFloor(px)) signs.push({ i, x: px, y: floor(px), style }); };
  for (const reef of of('reef')) post(1, reef.x0 + 140);
  for (const a of arches) post(2, a.x0 - 35);
  for (const e of entrances) post(3, e - 135);
  for (const sys of systems) { const c = sys[0].e; signs.push({ i: 4, x: c.x, y: chamberFloor(c, c.x), style: 'stone' }); const d = sys[sys.length - 1].e; signs.push({ i: 5, x: d.x, y: chamberFloor(d, d.x), style: 'stone' }); }
  for (const k of of('kelp')) post(6, k.x0 + 450);
  for (const h of hulls) post(7, Math.min(...h.pts.map(q => q[0])) - 160, 'bottle');
  for (const t of of('trench')) { post(8, t.x0 - 40); signs.push({ i: 9, x: (t.x0 + t.x1) / 2 - 60, y: floor((t.x0 + t.x1) / 2 - 60), style: 'bottle' }); }
  for (const t of temples) {
    const top = t.rooms.slice(0, Math.ceil(t.rooms.length / 3)).reduce((m, q) => (q.y < m.y ? q : m));
    signs.push({ i: 10, x: top.x + 60, y: top.y + top.h, style: 'stone' });
    signs.push({ i: 11, x: t.abyss.x + t.abyss.w / 2, y: t.abyss.y + t.abyss.h, style: 'stone' });
  }

  // The Old Turtle near the start; the Ghost Captain by the first wreck.
  const npcs: Npc[] = [];
  for (let tx = spawnX + 380; tx < spawnX + 900; tx += 40) if (!holeNear(tx) && !blockedFloor(tx)) { npcs.push({ id: 'turtle', x: tx, y: floor(tx) }); break; }
  if (hulls.length) {
    const xs = hulls[0].pts.map(q => q[0]);
    for (const cxp of [Math.max(...xs) + 340, Math.min(...xs) - 300]) if (!holeNear(cxp) && !blockedFloor(cxp)) { npcs.push({ id: 'captain', x: cxp, y: floor(cxp) }); break; }
  }

  return {
    w: W, h: H, spawn: { x: spawnX, y: floor(spawnX) - 380 }, floor, zones, mounds, ledges, arches, hulls,
    chambers, pockets, tunnels, entrances, caveBoxes, temples, gates, npcs, lamps, pearls, signs, chests, decorSeed: seed ^ 0x5eed,
  };
}

// ── A map, ready to draw and swim in ───────────────────────────────────────

export const GRID = 8;

export class World {
  readonly w: number;
  readonly h: number;
  readonly spawn: { x: number; y: number };
  readonly floorY: (x: number) => number;
  readonly zones: Zone[];
  readonly chambers: { id: ChamberId; e: Ellipse }[];
  readonly pockets: Ellipse[];
  readonly temples: Temple[];
  readonly gates: Gate[];
  readonly npcs: Npc[];
  readonly lamps: [number, number][];
  readonly pearls: Pearl[];
  readonly chests: Chest[];
  readonly signs: Sign[];
  readonly decor: Decor[];
  readonly areas: Area[];
  /** Every kind of area there is (for "areas discovered: n of …"). */
  readonly areaIds: AreaId[];
  /** Rough rectangles for the minimap's fog: an area can have several. */
  readonly bounds: [AreaId, [number, number, number, number]][];
  private d: Def;
  private grid: { data: Uint8Array; w: number; h: number } | null = null;

  constructor(d: Def) {
    this.d = d;
    this.w = d.w; this.h = d.h; this.spawn = d.spawn; this.floorY = d.floor;
    this.zones = d.zones; this.chambers = d.chambers; this.pockets = d.pockets; this.temples = d.temples; this.gates = d.gates; this.npcs = d.npcs; this.lamps = d.lamps;
    this.pearls = d.pearls.map(([x, y], i) => ({ id: `pearl${i + 1}`, x, y }));
    this.chests = d.chests.map((c, i) => ({ ...c, id: `c${c.tier}-${i + 1}` }));
    this.signs = d.signs;

    // Areas, checked in order: the first that contains a point is where you are.
    const inEllipse = (c: Ellipse, x: number, y: number) => ((x - c.x) / c.rx) ** 2 + ((y - c.y) / c.ry) ** 2 <= 1;
    const zoneAt = (x: number) => { let lo = 0, hi = d.zones.length - 1; while (lo < hi) { const m = (lo + hi + 1) >> 1; if (d.zones[m].x0 <= x) lo = m; else hi = m - 1; } return d.zones[lo]; };
    this.areas = [
      { id: 'abyss', inside: (x, y) => d.temples.some(t => inRect(t.abyss, x, y, 30)) },
      { id: 'temple', inside: (x, y) => d.temples.some(t => inRect(t.box, x, y)) },
      ...d.chambers.map(c => ({ id: c.id as AreaId, inside: (x: number, y: number) => inEllipse(c.e, x, y) })),
      { id: 'cabin', inside: (x, y) => d.hulls.some(h => x > h.cabin.x - 10 && x < h.cabin.x + h.cabin.w + 10 && y > h.cabin.y - 10 && y < h.cabin.y + h.cabin.h + 10) },
      { id: 'caves', inside: (x, y) => y > d.floor(x) + 30 && zoneAt(x).id !== 'trench' },
      ...(['shallows', 'meadow', 'reef', 'kelp', 'wreck', 'trench'] as ZoneId[]).map(id => ({ id: id as AreaId, inside: (x: number) => zoneAt(x).id === id })),
    ];
    this.areaIds = [...new Set(this.areas.map(a => a.id))];

    const deepest = (a: number, b: number) => { let m = 0; for (let x = a; x <= b; x += 40) m = Math.max(m, d.floor(x)); return m; };
    this.bounds = [];
    for (const z of d.zones) this.bounds.push([z.id, [z.x0, 0, z.x1, deepest(z.x0, z.x1) + 120]]);
    for (const c of d.caveBoxes) this.bounds.push(['caves', [c.x, c.y, c.x + c.w, c.y + c.h]]);
    for (const c of d.chambers) this.bounds.push([c.id, [c.e.x - c.e.rx, c.e.y - c.e.ry, c.e.x + c.e.rx, c.e.y + c.e.ry]]);
    for (const h of d.hulls) this.bounds.push(['cabin', [h.cabin.x, h.cabin.y, h.cabin.x + h.cabin.w, h.cabin.y + h.cabin.h]]);
    for (const t of d.temples) {
      this.bounds.push(['temple', [t.box.x, t.box.y, t.box.x + t.box.w, t.box.y + t.box.h]]);
      this.bounds.push(['abyss', [t.abyss.x - 40, t.abyss.y - 600, t.abyss.x + t.abyss.w + 40, t.abyss.y + t.abyss.h + 40]]);
    }

    this.decor = this.buildDecor();
  }

  /** Where you are: the first area (in order) that contains the point. */
  areaAt(x: number, y: number) { return this.areas.find(a => a.inside(x, y)) ?? this.areas[this.areas.length - 1]; }
  /** Inside a temple's rooms, halls or the Abyss (for brick walls). */
  templeSpace(x: number, y: number, pad = 0) {
    return this.d.temples.some(t => inRect(t.box, x, y, pad + 100) && (inRect(t.abyss, x, y, pad) || t.rooms.some(q => inRect(q, x, y, pad)) || t.halls.some(q => inRect(q, x, y, pad))));
  }

  // ── Shapes ──────────────────────────────────────────────────────────────
  traceFloorTop(g: CanvasRenderingContext2D, x0 = 0, x1 = this.w) {
    const a = Math.max(0, Math.floor(x0 / 16) * 16), b = Math.min(this.w, Math.ceil(x1 / 16) * 16);
    g.moveTo(a, this.floorY(a));
    for (let x = a + 16; x <= b; x += 16) g.lineTo(x, this.floorY(x));
  }
  traceFloor(g: CanvasRenderingContext2D, x0 = 0, x1 = this.w) {
    const a = Math.max(0, Math.floor(x0 / 16) * 16 - 16), b = Math.min(this.w, Math.ceil(x1 / 16) * 16 + 16);
    this.traceFloorTop(g, a, b);
    g.lineTo(b, this.h + 50);
    g.lineTo(a, this.h + 50);
    g.closePath();
  }
  /** Rock on the floor: mounds and trench ledges. */
  traceRocks(g: CanvasRenderingContext2D) {
    for (const m of [...this.d.mounds, ...this.d.ledges]) {
      g.moveTo(m.x + m.rx, m.y);
      g.ellipse(m.x, m.y, m.rx, m.ry, 0, 0, Math.PI * 2);
    }
  }
  /**
   * The rock arches. In a side view a solid arch would seal its own opening, so
   * they're scenery behind the water: drawn further back, and not solid.
   */
  archBoxes(): [number, number, number, number][] {
    return this.d.arches.map(a => [a.x0 - 20, a.top - 30, a.x1 + 20, Math.max(this.floorY(a.x0), this.floorY(a.x1)) + 40]);
  }
  traceArch(g: CanvasRenderingContext2D, i: number) {
    const a = this.d.arches[i];
    g.moveTo(a.x0, this.floorY(a.x0) + 30);
    g.bezierCurveTo(a.x0 - 10, a.top + 60, a.x0 + 30, a.top, (a.x0 + a.x1) / 2, a.top - 10);
    g.bezierCurveTo(a.x1 - 30, a.top, a.x1 + 10, a.top + 60, a.x1, this.floorY(a.x1) + 30);
    g.closePath();
  }
  /** An arch's opening, down to the sand (not into it). */
  traceArchHole(g: CanvasRenderingContext2D, i: number) {
    const h = this.d.arches[i].hole;
    g.moveTo(h.x0, this.floorY(h.x0) + 1);
    g.lineTo(h.x0, h.top + 60);
    g.bezierCurveTo(h.x0, h.top, h.x1, h.top, h.x1, h.top + 60);
    g.lineTo(h.x1, this.floorY(h.x1) + 1);
    for (let x = h.x1; x >= h.x0; x -= 8) g.lineTo(x, this.floorY(x) + 1);
    g.closePath();
  }
  traceHull(g: CanvasRenderingContext2D) {
    for (const h of this.d.hulls) {
      g.moveTo(h.pts[0][0], h.pts[0][1]);
      for (const [x, y] of h.pts.slice(1)) g.lineTo(x, y);
      g.closePath();
    }
  }
  /** The hulls' outlines as a shape you can test points against. */
  hullPath(): Path2D | null {
    if (!this.d.hulls.length) return null;
    const p = new Path2D();
    for (const h of this.d.hulls) {
      p.moveTo(h.pts[0][0], h.pts[0][1]);
      for (const [x, y] of h.pts.slice(1)) p.lineTo(x, y);
      p.closePath();
    }
    return p;
  }
  /** Underground spaces that are filled shapes: cave chambers, pockets, and the temples' rooms, halls and Abyss. */
  traceCaveCarves(g: CanvasRenderingContext2D, grow = 0) {
    for (const e of [...this.d.chambers.map(c => c.e), ...this.d.pockets]) {
      g.moveTo(e.x + e.rx + grow, e.y);
      g.ellipse(e.x, e.y, e.rx + grow, e.ry + grow, 0, 0, Math.PI * 2);
    }
    for (const t of this.d.temples) for (const q of [...t.rooms, ...t.halls, t.abyss]) g.rect(q.x - grow, q.y - grow, q.w + grow * 2, q.h + grow * 2);
  }
  strokeTunnels(g: CanvasRenderingContext2D, grow = 0) {
    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (const t of this.d.tunnels) {
      g.beginPath();
      const p = t.pts;
      g.moveTo(p[0][0], p[0][1]);
      for (let i = 1; i < p.length - 1; i++) g.quadraticCurveTo(p[i][0], p[i][1], (p[i][0] + p[i + 1][0]) / 2, (p[i][1] + p[i + 1][1]) / 2);
      g.lineTo(p[p.length - 1][0], p[p.length - 1][1]);
      g.lineWidth = t.w + grow * 2;
      g.stroke();
    }
  }
  /** The wrecks' cabins and doorways, cut out of the hulls. */
  traceCabin(g: CanvasRenderingContext2D, grow = 0) {
    for (const h of this.d.hulls) {
      g.roundRect(h.cabin.x - grow, h.cabin.y - grow, h.cabin.w + grow * 2, h.cabin.h + grow * 2, 26);
      g.roundRect(h.door.x - grow, h.door.y - grow, h.door.w + grow * 2, h.door.h + grow * 2, 20);
    }
  }

  // ── Collision ───────────────────────────────────────────────────────────
  /** The solids rasterised at 1/GRID scale (1 = solid), made once. */
  collision(): { data: Uint8Array; w: number; h: number } {
    if (this.grid) return this.grid;
    const gw = Math.ceil(this.w / GRID), gh = Math.ceil(this.h / GRID);
    const c = document.createElement('canvas');
    c.width = gw; c.height = gh;
    const g = c.getContext('2d', { willReadFrequently: true })!;
    g.scale(1 / GRID, 1 / GRID);
    g.fillStyle = '#000';
    g.beginPath(); this.traceFloor(g); this.traceRocks(g); g.fill('nonzero');
    g.beginPath(); this.traceHull(g); g.fill();
    g.globalCompositeOperation = 'destination-out';
    g.beginPath(); this.traceCaveCarves(g); g.fill();
    g.strokeStyle = '#000'; this.strokeTunnels(g);
    g.beginPath(); this.traceCabin(g); g.fill();
    const px = g.getImageData(0, 0, gw, gh).data;
    const data = new Uint8Array(gw * gh);
    for (let i = 0; i < data.length; i++) data[i] = px[i * 4 + 3] > 100 ? 1 : 0;
    this.grid = { data, w: gw, h: gh };
    return this.grid;
  }
  solidAt(x: number, y: number): boolean {
    if (x < 0 || x >= this.w || y >= this.h) return true;
    if (y < 0) return false;
    const { data, w } = this.collision();
    return data[Math.floor(y / GRID) * w + Math.floor(x / GRID)] === 1;
  }

  /**
   * Can a fish (radius ~22) reach every pearl, sign and chest from the start?
   * A flood fill over the collision grid with the walls grown by the fish's size.
   */
  allReachable(): boolean {
    const { data, w, h } = this.collision();
    const R = 3;   // cells: 24 units, the fish's radius
    const tmp = new Uint8Array(w * h), blocked = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let b = 0;
      for (let k = -R; k <= R && !b; k++) { const xx = x + k; if (xx < 0 || xx >= w || data[y * w + xx]) b = 1; }
      tmp[y * w + x] = b;
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      let b = y < 5 ? 1 : 0;
      for (let k = -R; k <= R && !b; k++) { const yy = y + k; if (yy >= h || (yy >= 0 && tmp[yy * w + x])) b = 1; }
      blocked[y * w + x] = b;
    }
    const seen = new Uint8Array(w * h);
    const sx = Math.floor(this.spawn.x / GRID), sy = Math.floor(this.spawn.y / GRID);
    if (blocked[sy * w + sx]) return false;
    const q = new Int32Array(w * h);
    let head = 0, tail = 0;
    q[tail++] = sy * w + sx; seen[sy * w + sx] = 1;
    while (head < tail) {
      const i = q[head++], x = i % w;
      if (x > 0 && !seen[i - 1] && !blocked[i - 1]) { seen[i - 1] = 1; q[tail++] = i - 1; }
      if (x < w - 1 && !seen[i + 1] && !blocked[i + 1]) { seen[i + 1] = 1; q[tail++] = i + 1; }
      if (i >= w && !seen[i - w] && !blocked[i - w]) { seen[i - w] = 1; q[tail++] = i - w; }
      if (i < w * (h - 1) && !seen[i + w] && !blocked[i + w]) { seen[i + w] = 1; q[tail++] = i + w; }
    }
    const near = (x: number, y: number, rad: number) => {
      const cx = Math.floor(x / GRID), cy = Math.floor(y / GRID), rc = Math.ceil(rad / GRID);
      for (let yy = cy - rc; yy <= cy + rc; yy++) for (let xx = cx - rc; xx <= cx + rc; xx++) {
        if (xx >= 0 && yy >= 0 && xx < w && yy < h && seen[yy * w + xx] && (xx - cx) ** 2 + (yy - cy) ** 2 <= rc * rc) return true;
      }
      return false;
    };
    return this.pearls.every(p => near(p.x, p.y, 40))
      && this.signs.every(s => near(s.x, s.y - 60, 120))
      && this.chests.every(c => near(c.x, c.y - 30, 70));
  }

  // ── Decorations ─────────────────────────────────────────────────────────
  private buildDecor(): Decor[] {
    const d = this.d;
    const r = rng(d.decorSeed);
    const out: Decor[] = [];
    const add = (kind: DecorKind, x: number, y: number, s = 1, up = false) =>
      out.push({ kind, x, y, s, seed: Math.floor(r() * 1e6), color: Math.floor(r() * 6), up });
    const holes = d.entrances.map(e => [e - 110, e + 110]);
    const overHole = (x: number) => holes.some(([a, b]) => x > a && x < b);
    const f = this.floorY;
    const scatter = (x0: number, x1: number, n: number, kinds: [DecorKind, number][], sMin = 0.7, sMax = 1.3) => {
      const total = kinds.reduce((a, [, w]) => a + w, 0);
      for (let i = 0; i < n; i++) {
        const x = x0 + r() * (x1 - x0);
        if (overHole(x)) { r(); r(); continue; }
        let pick = r() * total, kind = kinds[0][0];
        for (const [k, w] of kinds) { if ((pick -= w) <= 0) { kind = k; break; } }
        add(kind, x, f(x) + 6, sMin + r() * (sMax - sMin));
      }
    };
    for (const z of d.zones) {
      const w = z.x1 - z.x0;
      if (z.id === 'shallows') {
        scatter(z.x0, z.x1, w / 23, [['grass', 6], ['rock', 2], ['star', 1], ['shell', 1], ['brain', 0.6]]);
        for (let x = z.x1 - 400; x < z.x1 - 100; x += 70) if (!overHole(x)) add('kelp', x, f(x) + 8, 0.35 + r() * 0.2);
      } else if (z.id === 'meadow') scatter(z.x0, z.x1, w / 10, [['grass', 10], ['rock', 1], ['shell', 0.6]], 0.9, 1.6);
      else if (z.id === 'reef') scatter(z.x0, z.x1, w / 16, [['branch', 3], ['fan', 2], ['brain', 2], ['anemone', 2], ['sponge', 1.5], ['grass', 2], ['star', 0.5]]);
      else if (z.id === 'kelp') {
        for (let x = z.x0 + 20; x < z.x1 - 20; x += 38 + r() * 40) if (!overHole(x)) add('kelp', x, f(x) + 8, 0.7 + r() * 0.6);
        scatter(z.x0, z.x1, w / 32, [['grass', 3], ['rock', 2], ['shell', 1]]);
      } else if (z.id === 'wreck') scatter(z.x0, z.x1, w / 34, [['rock', 3], ['grass', 1], ['worms', 1], ['shell', 1]]);
      else {
        scatter(z.x0 + 300, z.x1 - 300, w / 38, [['worms', 3], ['vent', 1], ['rock', 2], ['glow', 2]]);
        const m = (z.x0 + z.x1) / 2;
        for (const x of [m - 80, m + 140]) add('vent', x, f(x) + 4, 1.2);
      }
    }
    for (const m of d.mounds) for (let i = 0; i < 6; i++) {
      const x = m.x - m.rx * 0.7 + r() * m.rx * 1.4;
      add((['branch', 'fan', 'brain', 'anemone'] as DecorKind[])[i % 4], x, m.y - m.ry * Math.sqrt(Math.max(0, 1 - ((x - m.x) / m.rx) ** 2)) + 6, 0.7 + r() * 0.4);
    }
    for (const h of d.hulls) {
      const [[ax, ay], [bx, by]] = h.deck[0][0] < h.deck[1][0] ? h.deck : [h.deck[1], h.deck[0]];
      const mx = (ax + bx) / 2 - 20;
      add('mast', mx, ay + ((mx - ax) / (bx - ax)) * (by - ay), 1);
      const xs = h.pts.map(p => p[0]);
      const lo = Math.min(...xs), hi = Math.max(...xs);
      add('barrel', lo - 140, f(lo - 140) + 4, 1); add('barrel', hi + 70, f(hi + 70) + 4, 0.85); add('anchor', hi + 210, f(hi + 210) + 4, 1);
    }
    for (const e of [...d.chambers.map(c => c.e), ...d.pockets]) {
      for (let i = 0; i < 9; i++) {
        const x = e.x - e.rx * 0.85 + r() * e.rx * 1.7;
        if (i < 5) add('crystal', x, chamberFloor(e, x) + 6, 0.6 + r() * 0.7);
        else add('stalactite', x, chamberCeiling(e, x) - 6, 0.6 + r() * 0.6, true);
      }
    }
    for (const t of d.tunnels) for (const [x, y] of t.pts.slice(1, -1)) add('crystal', x + 30, y + t.w / 2 - 4, 0.5);
    // The temples: columns and old urns in the rooms, and glowing motes in the Abyss.
    for (const t of d.temples) {
      for (const q of t.rooms) {
        add('pillar', q.x + 40, q.y + q.h + 4, 0.9 + r() * 0.2); add('pillar', q.x + q.w - 40, q.y + q.h + 4, 0.9 + r() * 0.2);
        if (r() < 0.6) add('urn', q.x + q.w * (0.3 + r() * 0.4), q.y + q.h + 4, 0.8 + r() * 0.4);
      }
      for (let i = 0; i < 6; i++) add('glow', t.abyss.x + 80 + r() * (t.abyss.w - 160), t.abyss.y + t.abyss.h - 10, 1);
      add('pillar', t.abyss.x + 60, t.abyss.y + t.abyss.h + 4, 1.4); add('pillar', t.abyss.x + t.abyss.w - 60, t.abyss.y + t.abyss.h + 4, 1.4);
    }
    return out.sort((a, b) => a.x - b.x);
  }
}

// ── One map per seed ───────────────────────────────────────────────────────

const cache = new Map<string, World>();
/** The map for a class without a seed (and the dev demo). */
const DEFAULT_SEED = 20261006;

/**
 * The map for a class. A generated map that fails its checks is replaced by the
 * next variation of the same seed, so it's still the same for everyone in the class.
 */
export function worldFor(seed: number | null | undefined): World {
  const s = seed ?? DEFAULT_SEED;
  const key = String(s);
  const hit = cache.get(key);
  if (hit) return hit;
  let world: World | null = null;
  let last: World | null = null;
  for (let k = 0; k < 12 && !world; k++) {
    const w = new World(generate((s + k * 7919) >>> 0));
    last = w;
    if (w.chambers.length >= 7 && w.temples.length === 2 && w.pearls.length === 60 && w.allReachable()) world = w;
  }
  world ??= last!;
  cache.set(key, world);
  return world;
}
